import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useSetSlackOverride } from "@/hooks/useSupportDepartments";
import type { Department, DepartmentMember } from "@/services/supportDepartmentsService";

import { MemberOverrideActions } from "./MemberOverrideActions";
import { SlackIdInput } from "./SlackIdInput";

function slackStatus(m: DepartmentMember): { label: string; style: string } {
  if (m.slack_user_id_override) return { label: "Override", style: "border-coral/40 text-coral-dark" };
  if (m.slack_user_id) return { label: "Linked", style: "border-teal/50 text-teal-dark" };
  return { label: "Missing", style: "border-destructive/40 text-destructive" };
}

export function MemberRow({ member, department }: { member: DepartmentMember; department: Department }) {
  const setOverride = useSetSlackOverride();
  const status = slackStatus(member);
  // Teamwork's own lead flag wins the label; a Core lead only counts while
  // the person is active (a dormant one is listed separately).
  const coreLead = !member.is_lead && member.is_active && Boolean(member.lead_in_core);

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2 py-2",
        !member.is_active && "opacity-60",
      )}
    >
      <div className="min-w-[10rem] flex-1">
        <p className="text-sm font-medium text-foreground">{member.name}</p>
        <p className="text-xs text-muted-foreground">{member.role_name}</p>
      </div>
      {member.is_lead ? (
        <Badge variant="outline" className="border-coral/40 text-coral">Lead (Teamwork)</Badge>
      ) : coreLead ? (
        <Badge variant="outline" className="border-coral/40 text-coral">Lead (Core)</Badge>
      ) : null}
      <Badge variant="outline" className="text-muted-foreground">Teamwork</Badge>
      {!member.is_active ? <Badge variant="outline">Inactive</Badge> : null}
      <Badge variant="outline" className={status.style}>
        {status.label}
      </Badge>
      <SlackIdInput
        label={`Slack override for ${member.name}`}
        value={member.slack_user_id_override}
        placeholder={member.slack_user_id ?? "U0123ABCD"}
        onSave={(slackId) =>
          setOverride.mutateAsync({ personId: member.teamwork_person_id, slackId })
        }
      />
      <MemberOverrideActions member={member} department={department} />
    </li>
  );
}
